import type { CheckboxChangeEventType, DynamicFieldComponentProps } from '@myosh/odin-components';
import { Checkbox } from '@myosh/odin-components';
import clsx from 'clsx';
import { useEffect, useReducer, useRef, useState } from 'react';
import type { Control, FieldError } from 'react-hook-form';
import { useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { ExtendedDynamicFormHierarchySettings } from '../../../@types/forms';
import type {
  HierarchyDropdownValue,
  HierarchyType,
  TransformedFormHierarchyTypes,
} from '../../../@types/hierarchy-fields';
import { useGetCurrentSchemaInfoQuery } from '../../../redux/services/api';
import { useHierarchyTypesQuery, useLazyHierarchyValuesQuery } from '../../../redux/services/hierarchy';
import useGetPersonHierarchies from '../person/use-get-person-hierarchy';
import { HierarchiesActionType, HierarchiesReducer } from './hierarchies.reducer';
import HierarchyFieldComponent from './hierarchy-field.component';
import { isHierarchyKeyValueEqual, setNewParentPersonHierarchyValues } from './hierarchy-functions';

export type RecordHierarchyFields = TransformedFormHierarchyTypes | HierarchyType;

export type HierarchyFieldError = Record<string, Record<string, FieldError>>;

export interface HierarchyFieldGroupProps
  extends DynamicFieldComponentProps<Record<string, HierarchyDropdownValue | HierarchyDropdownValue[]>> {
  recordId?: number;
  hierarchySettings?: ExtendedDynamicFormHierarchySettings;
  hierarchyTypes?: Array<TransformedFormHierarchyTypes | HierarchyType>;
  isUserHierarchy?: boolean;
  isExternal?: boolean;
  isNewRecord?: boolean;
  errorObject?: HierarchyFieldError;
  isCompetencyHierarchy?: boolean;
  ignoreHierarchyAccess?: boolean;
  usesMultiSelectDropdowns?: boolean;
  control?: Control;
  showAddNewOption?: boolean;
}

export function HierarchyFieldGroup({
  errorObject,
  value = {},
  hierarchyTypes,
  readOnly,
  onChange,
  hierarchySettings,
  isUserHierarchy = false,
  isExternal,
  isNewRecord,
  isCompetencyHierarchy = false,
  ignoreHierarchyAccess,
  usesMultiSelectDropdowns = false,
  control,
  showAddNewOption,
}: HierarchyFieldGroupProps) {
  const { data: schemaInfo } = useGetCurrentSchemaInfoQuery();
  const [{ dependentHierarchyTypes, hierarchyFieldsState, fieldsValues }, dispatch] = useReducer(
    HierarchiesReducer,
    {}
  );

  const { data: hierachyTypesAll } = useHierarchyTypesQuery({ archived: 'false' });
  const [getHierarchyValues] = useLazyHierarchyValuesQuery();
  const [ignoreHierarchyAccessValue, setIgnoreHierarchyAccessValue] = useState<boolean>(Boolean(ignoreHierarchyAccess));
  const hasChangedValues = useRef(false);
  const hasCompletedInitialisation = useRef(false);
  const { t } = useTranslation();
  const personFieldId = hierarchySettings?.autoPopulateHierarchyFromPersonFieldId;
  const isAdvancedHierarchiesEnabled = hierarchySettings?.enableAdvance;

  useEffect(() => {
    if (!hasCompletedInitialisation.current && value && hierarchyFieldsState && dependentHierarchyTypes) {
      hasCompletedInitialisation.current = true;
      dispatch({
        type: HierarchiesActionType.SetInitialFieldsValue,
        value,
        usesMultiSelectDropdowns,
        isAdvancedHierarchiesEnabled,
        isNewRecord,
      });
    }
  }, [value, hierarchyFieldsState, dependentHierarchyTypes]);

  useEffect(() => {
    if (!usesMultiSelectDropdowns && fieldsValues && !isHierarchyKeyValueEqual(value, fieldsValues)) {
      dispatch({
        type: HierarchiesActionType.SetInitialFieldsValue,
        value,
        usesMultiSelectDropdowns,
      });
    }
  }, [value]);

  useEffect(() => {
    if (hierarchyTypes?.length && hierachyTypesAll?.length && schemaInfo) {
      dispatch({
        isCompetencyHierarchy,
        type: HierarchiesActionType.SetInitialFieldsConfigurations,
        hierarchyTypes,
        isUserHierarchy,
        isExternal,
        hierachyTypesAll,
        isTreeHierarchy: schemaInfo['tree-hierarchies-enabled'] === 'true',
      });
    }
  }, [hierarchyTypes, hierachyTypesAll, schemaInfo]);

  const onChangeHandler = (
    hierarchyField: RecordHierarchyFields,
    fieldValue: HierarchyDropdownValue | HierarchyDropdownValue[]
  ) => {
    if (hierarchyField?.hierarchyTypeConfigDTO?.showAll) {
      getHierarchyValues({
        page: 1,
        pageSize: -1,
        filters: {
          archived: { value: false, comparison: '=' },
        },
      })
        .unwrap()
        .then((result) => {
          dispatch({
            type: HierarchiesActionType.UpdateFieldValue,
            hierarchyField,
            fieldValue,
            usesMultiSelectDropdowns,
            isAdvancedHierarchiesEnabled,
            allHierarchyValues: result.items,
          });
        });
    } else {
      dispatch({
        type: HierarchiesActionType.UpdateFieldValue,
        hierarchyField,
        fieldValue,
        usesMultiSelectDropdowns,
        isAdvancedHierarchiesEnabled,
      });
    }

    hasChangedValues.current = true;
  };

  useEffect(() => {
    if (fieldsValues && hasChangedValues.current) {
      onChange?.(fieldsValues);
      hasChangedValues.current = false;
    }
  }, [fieldsValues, ignoreHierarchyAccessValue, onChange]);

  const { hierarchies: personHierarchies, selectedPersonId } = useGetPersonHierarchies({
    control,
    parentFieldId: personFieldId,
  });
  const { dirtyFields } = useFormState({
    control,
  });

  // Update value only if parent person field has been actually changed
  useEffect(() => {
    if (personFieldId && dirtyFields[String(personFieldId)] && selectedPersonId) {
      const updatedPersonHierarchyValues = setNewParentPersonHierarchyValues(
        personHierarchies,
        hierarchyFieldsState,
        fieldsValues,
        isUserHierarchy,
        isCompetencyHierarchy
      );
      dispatch({
        type: HierarchiesActionType.SetInitialFieldsValue,
        value: updatedPersonHierarchyValues,
      });
      hasChangedValues.current = true;
    }
  }, [personHierarchies, personFieldId, selectedPersonId]);

  const hierarchyStyles = clsx({ hidden: hierarchySettings?.hiddenInReadMode && readOnly });

  return (
    <>
      {isCompetencyHierarchy && (
        <div>
          <Checkbox
            name="ignore-hierarchy-access"
            label={t('ignore-hierarchy-access')}
            initialChecked={ignoreHierarchyAccessValue}
            onChange={(event?: CheckboxChangeEventType) => {
              if (event && setIgnoreHierarchyAccessValue) {
                hasChangedValues.current = true;
                setIgnoreHierarchyAccessValue(event.checked);
                dispatch({ type: HierarchiesActionType.SetIgnoreHIerarchyAccess, ignoreHierarchyAccess: event });
              }
            }}
            readOnly={readOnly}
          />
        </div>
      )}
      {!ignoreHierarchyAccessValue && (
        <div className={hierarchyStyles}>
          {hierarchyFieldsState?.map((hierarchyField) => (
            <HierarchyFieldComponent
              key={hierarchyField.id}
              hierarchySettings={hierarchySettings}
              value={fieldsValues}
              hierarchyField={hierarchyField}
              readOnly={readOnly}
              errorObject={errorObject}
              isAdvancedHierarchiesEnabled={isAdvancedHierarchiesEnabled}
              advancedHierarchiesConfig={hierarchyField?.hierarchyTypeConfigDTO}
              isUserHierarchy={isUserHierarchy}
              onChange={onChangeHandler}
              isCompetencyHierarchy={isCompetencyHierarchy}
              usesMultiSelectDropdowns={usesMultiSelectDropdowns}
              showAddNewOption={showAddNewOption}
            />
          ))}
        </div>
      )}
    </>
  );
}
